import { Markdown } from '@cibo/ui'
import UploadIcon from '@mui/icons-material/UploadFile'
import { LoadingButton } from '@mui/lab'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  InputLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import { FileRejection } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { useUploadGeometry } from './GeometryUploadContext'

type ErrorState = { rejections: FileRejection[] }

export const GeometryUpload = ({
  resourceId,
  dialogOpen,
  setDialogOpen,
}: {
  resourceId: string
  dialogOpen: boolean
  setDialogOpen: (arg: boolean) => void
}) => {
  const { t } = useTranslation('mapgl/GeometryUpload')

  const [uploadError, setError] = useState<ErrorState | undefined>()
  const [fieldName, setFieldName] = useState<string>()

  const onBusy = () => null
  const handleError = () => null

  const handleClose = () => {
    setDialogOpen(false)
    includedAcceptedFiles.forEach(({ name }) => excludeFile(name))
    setError(undefined)
    setFieldName(undefined)
  }

  const handleFilesRejected = (rejections: FileRejection[]) => {
    setError({ rejections })
  }

  const onUploadResponse = (response: any) => {
    handleClose()
  }

  const {
    busy,
    dropzone: { getRootProps, getInputProps, isDragActive, open },
    error,
    handleUpload,
    excludeFile,
    includedAcceptedFiles,
  } = useUploadGeometry(resourceId, onBusy, handleError, onUploadResponse, handleFilesRejected)

  const handleRemoveFile = (filename: string) => () => {
    excludeFile(filename)
    setError(undefined)
    setFieldName(undefined)
  }

  return (
    <Dialog onClose={handleClose} open={dialogOpen} maxWidth="md">
      <DialogTitle>{t('uploadShapeFile')}</DialogTitle>
      <DialogContent>
        <div {...getRootProps()}>
          <Box sx={{ paddingBottom: 2 }}>
            <Markdown>{t('uploadShapeFileDescription')}</Markdown>
          </Box>
          <Stack spacing={2}>
            {isDragActive ? (
              <Box p={5} sx={{ border: '2px dashed gray' }} alignItems="center">
                <Stack direction="row" alignItems="center">
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {t('drop')}
                  </Typography>
                </Stack>
              </Box>
            ) : (
              <Box>
                <Divider />
                <Stack
                  m={4}
                  spacing={2}
                  direction={{ xs: 'column', md: 'row' }}
                  justifyContent="center"
                  alignItems="center"
                >
                  {/* @ts-ignore types of property 'capture' are incompatible */}
                  <input {...getInputProps()} data-testid="upload_input" />
                  {!includedAcceptedFiles?.length ? (
                    <>
                      <Button variant="contained" onClick={open}>
                        {t('selectFile')}
                      </Button>
                      <Typography>{t('or')}</Typography>{' '}
                      <Stack>
                        <Stack spacing={2} direction="row" alignItems="center" paddingTop="20px">
                          <UploadIcon sx={{ color: 'text.secondary', fontSize: 40 }} />
                          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            {t('dropFileHere')}
                          </Typography>
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          {t('buttonInstructions')}
                        </Typography>
                      </Stack>
                    </>
                  ) : (
                    <Stack spacing={2}>
                      <Stack direction="row" spacing={4} alignItems="flex-end">
                        <Stack spacing={2}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <InputLabel>{t('file')}</InputLabel>
                            {includedAcceptedFiles?.map((file: { name: string }) => (
                              <Chip
                                key={file.name}
                                label={file.name}
                                variant="outlined"
                                onDelete={handleRemoveFile(file.name)}
                                disabled={busy}
                              />
                            ))}
                          </Stack>

                          <Stack spacing={1} width={300}>
                            <TextField
                              label={t('fieldName')}
                              id="fieldName"
                              data-testid="field-name-input"
                              value={fieldName}
                              onChange={({ target: { value } }) => {
                                setFieldName(value)
                              }}
                              disabled={!includedAcceptedFiles?.length || !!error}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {t('fieldNameDescription')}
                            </Typography>
                          </Stack>
                        </Stack>
                        <LoadingButton
                          onClick={e => handleUpload(e, fieldName)}
                          disabled={!includedAcceptedFiles?.length || !!error}
                          loading={busy}
                          color="secondary"
                          variant="contained"
                          data-testid="commit_shapefile"
                        >
                          {t('upload')}
                        </LoadingButton>
                      </Stack>
                    </Stack>
                  )}
                </Stack>

                <Divider />
                {(uploadError || !!error) && (
                  <Alert severity="error" sx={{ marginTop: 2 }}>
                    <AlertTitle>{t('Error')}</AlertTitle>
                    {uploadError?.rejections?.map(({ file }: FileRejection) => (
                      <Chip
                        color="error"
                        key={file.name}
                        label={file.name}
                        variant="outlined"
                        onDelete={() => handleRemoveFile(file.name)()}
                      />
                    ))}
                    {error && error?.response?.data.message}
                  </Alert>
                )}
              </Box>
            )}
          </Stack>
        </div>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={handleClose}>
          {t('close')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
